import { SelectProps } from "antd";
import { BaseOptionType } from "antd/lib/select";
import { Select } from "components/styleguide";
import { TimezoneModel } from "generated/api";
import { FC, ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { buildTimezoneOptions, TimezoneDisplayOption } from "utils/timezoneDisplayUtils";

import styles from "./styles.module.less";

interface TimezoneSelectProps extends Omit<SelectProps<string>, "children" | "options"> {
  timezones?: TimezoneModel[] | null;
  currentTimezoneId?: string | null;
  currentTimezoneDescription?: string | null;
}

interface TimezoneSelectOption extends BaseOptionType {
  value: string;
  label: ReactElement;
  displayLabel: string;
  searchText: string;
  title: string;
}

const renderTimezoneOptionContent = ({ territory, utcReference }: TimezoneDisplayOption) => (
  <div className={styles.timezoneOption}>
    <div className={styles.territory} title={territory}>
      {territory}
    </div>
    <div className={styles.utcReference} title={utcReference}>
      {utcReference}
    </div>
  </div>
);

const TimezoneSelect: FC<TimezoneSelectProps> = ({
  timezones,
  currentTimezoneId,
  currentTimezoneDescription,
  dropdownClassName,
  dropdownRender,
  dropdownStyle,
  dropdownMatchSelectWidth,
  optionFilterProp,
  optionLabelProp,
  showSearch,
  ...props
}) => {
  const { t } = useTranslation();
  const resolvedDropdownClassName = [styles.dropdown, dropdownClassName].filter(Boolean).join(" ");

  const timezoneOptions = useMemo(
    () =>
      buildTimezoneOptions(timezones, {
        selectedTimezoneId: currentTimezoneId,
        selectedTimezoneDescription: currentTimezoneDescription,
      }),
    [currentTimezoneDescription, currentTimezoneId, timezones],
  );

  const selectOptions = useMemo<TimezoneSelectOption[]>(
    () =>
      timezoneOptions.map((option) => ({
        value: option.id,
        label: renderTimezoneOptionContent(option),
        displayLabel: `${option.territory} | ${option.utcReference}`,
        searchText: option.searchText,
        title: option.searchText,
      })),
    [timezoneOptions],
  );

  const renderDropdown = (menu: ReactElement) => {
    const dropdown = (
      <div>
        <div className={styles.dropdownHeader} aria-hidden="true">
          <span>{t("Timezone")}</span>
          <span>{t("UTC reference")}</span>
        </div>
        {menu}
      </div>
    );

    return dropdownRender ? dropdownRender(dropdown) : dropdown;
  };

  return (
    <Select
      {...props}
      dropdownClassName={resolvedDropdownClassName}
      dropdownMatchSelectWidth={dropdownMatchSelectWidth ?? true}
      dropdownRender={renderDropdown}
      dropdownStyle={dropdownStyle}
      optionFilterProp={optionFilterProp ?? "searchText"}
      optionLabelProp={optionLabelProp ?? "displayLabel"}
      options={selectOptions}
      showSearch={showSearch ?? true}
    />
  );
};

export default TimezoneSelect;
